import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { titleize } from 'underscore.string';

import _ from 'lodash';

import { Map } from 'components/shipment/maps/map';
import Alert from 'components/ui/Alert';
import ErrorAlert from 'components/ui/ErrorAlert';
import ProTip from 'components/ui/ProTip';
import { Translation, withTranslation } from 'react-i18next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getEmptyLeg } from 'helpers/trip-helper';
import InfoIcon from 'components/v2/icons/InfoIcon';
import Leg from './Leg';

import TripPlannerEquipmentMatrix from './TripPlannerEquipmentMatrix';

const center = {
  lat: 34.0522,
  lng: -118.2437,
}; // Temp until we implement the map features

const equipmentTip = (
  <Tooltip id="equipments-tip">
    <Translation>{(t) => t('TRIP_EQUIPMENT_TIP')}</Translation>
  </Tooltip>
);

class TripPlannerForm extends Component {
  static propTypes = {
    carrier: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
    }),
    equipments: PropTypes.array,
    shipperId: PropTypes.string,
    trip: PropTypes.shape({
      name: PropTypes.string,
      legs: PropTypes.arrayOf(
        PropTypes.shape({
          carrier_id: PropTypes.string,
          carrier: PropTypes.string,
          stops: PropTypes.arrayOf(
            PropTypes.shape({
              action: PropTypes.oneOf(['pickup', 'deliver']),
              empty: PropTypes.bool,
              location: PropTypes.shape({
                address: PropTypes.string,
                name: PropTypes.string,
              }),
            }),
          ),
        }),
      ),
    }),
    formStatus: PropTypes.shape({
      pending: PropTypes.bool.isRequired,
      completed: PropTypes.bool.isRequired,
      success: PropTypes.bool.isRequired,
      error: PropTypes.any,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
    onCreateTrip: PropTypes.func.isRequired,
    onUpdateTrip: PropTypes.func.isRequired,
    onSaveAndApplyTrip: PropTypes.func.isRequired,
  };

  static defaultProps = {
    equipments: [],
    trip: {
      name: '',
      legs: [
        getEmptyLeg({
          sequence: 1,
        }),
      ],
    },
  };

  constructor(props) {
    super(props);

    const trip = props.trip
      ? { ...props.trip }
      : {
          name: '',
          legs: [
            getEmptyLeg({
              carrier: props.carrier,
              sequence: 1,
            }),
          ],
        };

    const { formStatus } = props;

    this.state = {
      trip,
      showSaveAsSection: false,
      validationResult: {
        hasErrors: false,
        legs: [],
      },
      formStatus,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { trip: currentTrip, formStatus: currentFormStatus } = this.props;

    const { trip: nextTrip, formStatus: nextFormStatus } = nextProps;

    if (currentTrip._id !== nextTrip._id) {
      const updatedTrip = { ...nextProps.trip };
      this.setState({
        trip: updatedTrip,
      });
    }

    if (!_.isEqual(currentFormStatus, nextFormStatus)) {
      this.setState((prevState) => {
        const showSaveAsSection = nextFormStatus.success
          ? false
          : prevState.showSaveAsSection;
        return {
          formStatus: nextFormStatus,
          showSaveAsSection,
        };
      });
    }
  }

  getHeading() {
    const {
      trip: { _id, name },
      t,
    } = this.props;

    if (_id) {
      return t('UPDATE_WITH_VALUE', { name });
    }
    return t('CREATE_NEW_TRIP');
  }

  handleCloseErrorAlert = () =>
    this.setState({
      validationResult: {
        hasErrors: false,
        legs: [],
      },
    });

  handleCloseSaveErrorAlert = () =>
    this.setState((prevState) => {
      const { formStatus: currentFormStatus } = prevState;

      const formStatus = {
        ...currentFormStatus,
        completed: false,
        error: null,
      };
      return {
        formStatus,
      };
    });

  handleCloseSuccessAlert = () =>
    this.setState((prevState) => {
      const { formStatus: currentFormStatus } = prevState;

      const formStatus = {
        ...currentFormStatus,
        completed: false,
      };

      return {
        formStatus,
      };
    });

  handleSaveClick = () => {
    this.setState(
      (prevState) => {
        const validationResult = this.validate(prevState);
        return {
          validationResult,
        };
      },
      () => {
        if (!this.state.validationResult.hasErrors) {
          const payload = this.transformPayload();
          this.props.onUpdateTrip(payload);
        }
      },
    );
  };

  handleSaveAsClick = () =>
    this.setState({
      showSaveAsSection: true,
    });

  handleSaveTrip = () => {
    this.setState(
      (prevState) => {
        const validationResult = this.validate(prevState);
        return {
          validationResult,
        };
      },
      () => {
        if (!this.state.validationResult.hasErrors) {
          const payload = this.transformPayload();
          if (!payload._id) {
            this.props.onCreateTrip(payload);
          } else {
            this.props.onUpdateTrip(payload);
          }
        }
      },
    );
  };

  handleSaveAndApplyTrip = () => {
    this.setState(
      (prevState) => {
        const validationResult = this.validate(prevState, true);
        return {
          validationResult,
        };
      },
      () => {
        if (!this.state.validationResult.hasErrors) {
          const payload = this.transformPayload();
          this.props.onSaveAndApplyTrip(payload);
        }
      },
    );
  };

  handleTripNameChange = (e) => {
    const {
      target: { value: name },
    } = e;
    this.setState((prevState) => ({
      trip: {
        ...prevState.trip,
        name,
      },
    }));
  };

  handleLegEquipmentAdd = (legSequence, equipmentId) => {
    this.setState((prevState) => {
      const {
        trip: { legs },
      } = prevState;

      const index = legs.findIndex((leg) => leg.sequence === legSequence);
      const leg = _.cloneDeep(legs[index]);

      const equipment = this.props.equipments.find(
        (eq) => eq._id === equipmentId,
      );

      leg.stops = leg.stops.map((stop) => {
        const stopEquipments = stop.equipments || [];
        if (!stopEquipments.find((eq) => eq._id === equipmentId)) {
          return {
            ...stop,
            equipments: [...stopEquipments, equipment],
          };
        }
        return stop;
      });

      return {
        trip: {
          ...prevState.trip,
          legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)],
        },
      };
    });
  };

  handleCarrierFieldChange = (legSequence, name) => {
    this.setState((prevState) => {
      const {
        trip: { legs },
      } = prevState;
      const index = legs.findIndex((l) => l.sequence === legSequence);
      const leg = _.cloneDeep(legs[index]);
      leg.carrier = name;
      leg.carrier_id = null;

      return {
        trip: {
          ...prevState.trip,
          legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)],
        },
      };
    });
  };

  handleCarrierRemove = (legSequence) =>
    this.setState((prevState) => {
      const {
        trip: { legs },
      } = prevState;
      const index = legs.findIndex((l) => l.sequence === legSequence);
      const leg = _.cloneDeep(legs[index]);
      leg.carrier = '';
      leg.carrier_id = null;

      return {
        trip: {
          ...prevState.trip,
          legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)],
        },
      };
    });

  handleCarrierSelect = (legSequence, carrier) => {
    this.setState((prevState) => {
      const {
        trip: { legs },
      } = prevState;
      const index = legs.findIndex((l) => l.sequence === legSequence);
      const leg = _.cloneDeep(legs[index]);
      leg.carrier = carrier.name;
      leg.carrier_id = carrier._id;

      return {
        trip: {
          ...prevState.trip,
          legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)],
        },
      };
    });
  };

  handleLegAdd = () =>
    this.setState((prevState) => ({
      trip: {
        ...prevState.trip,
        legs: [
          ...prevState.trip.legs,
          getEmptyLeg({
            carrier: this.props.carrier,
            sequence: prevState.trip.legs.length + 1,
          }),
        ],
      },
    }));

  handleLegRemove = (legSequence) =>
    this.setState((prevState) => {
      const {
        trip: { legs: currentLegs },
      } = prevState;

      const index = currentLegs.findIndex((l) => l.sequence === legSequence);

      const legs = [
        ...currentLegs.slice(0, index),
        ...currentLegs.slice(index + 1),
      ].map((leg, i) => ({
        ...leg,
        sequence: i + 1,
      }));

      return {
        trip: {
          ...prevState.trip,
          legs,
        },
      };
    });

  handleLegTypeChange = (legSequence, shipment_type) => {
    this.setState((prevState) => {
      const {
        trip: { legs },
      } = prevState;

      const index = legs.findIndex((l) => l.sequence === legSequence);
      const leg = _.cloneDeep(legs[index]);

      leg.shipment_type = shipment_type;
      const stops = leg.stops || [];

      if (shipment_type === 'rail' && stops.length === 3) {
        const lastStop = leg.stops[2];
        const location = lastStop.location || { name: '', address: '' };
        const stopFilled =
          location.name || location.address || lastStop.equipments.length > 0;

        if (!stopFilled) {
          leg.stops = [...leg.stops.slice(0, 2)];
        }
      } else if (shipment_type === 'drayage' && stops.length === 2) {
        leg.stops = [
          ...leg.stops,
          {
            sequence: 3,
            action: 'deliver',
            empty: true,
            equipments: [],
            location: [],
          },
        ];
      }
      return {
        trip: {
          ...prevState.trip,
          legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)],
        },
      };
    });
  };

  handleStopAdd = (legSequence) => {
    this.setState((prevState) => {
      const {
        trip: { legs },
      } = prevState;

      const legIndex = legs.findIndex((l) => l.sequence === legSequence);
      const leg = _.cloneDeep(legs[legIndex]);

      const sequence = leg.stops.length + 1;
      leg.stops = [
        ...leg.stops,
        {
          sequence,
          action: '',
          equipments: [],
          location: [
            {
              name: '',
              address: '',
            },
          ],
        },
      ];
      return {
        trip: {
          ...prevState.trip,
          legs: [...legs.slice(0, legIndex), leg, ...legs.slice(legIndex + 1)],
        },
      };
    });
  };

  handleStopEquipmentAdd = (legSequence, stopSequence, equipmentId) => {
    this.setState((prevState) => {
      const {
        trip: { legs },
      } = prevState;

      const index = legs.findIndex((leg) => leg.sequence === legSequence);
      const leg = _.cloneDeep(legs[index]);

      const stopIndex = leg.stops.findIndex(
        (stop) => stop.sequence === stopSequence,
      );

      const stop = {
        ...leg.stops[stopIndex],
      };

      const equipments = stop.equipments || [];
      if (equipments.findIndex((eq) => eq._id === equipmentId) === -1) {
        const equipment = this.props.equipments.find(
          (eq) => eq._id === equipmentId,
        );
        stop.equipments = equipments.concat(equipment);

        leg.stops = [
          ...leg.stops.slice(0, stopIndex),
          stop,
          ...leg.stops.slice(stopIndex + 1),
        ];

        return {
          trip: {
            ...prevState.trip,
            legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)],
          },
        };
      }
      return null;
    });
  };

  handleStopEquipmentRemove = (legSequence, stopSequence, equipmentId) => {
    this.setState((prevState) => {
      const {
        trip: { legs },
      } = prevState;

      const index = legs.findIndex((leg) => leg.sequence === legSequence);
      const leg = _.cloneDeep(legs[index]);

      const stopIndex = leg.stops.findIndex(
        (stop) => stop.sequence === stopSequence,
      );
      const stop = {
        ...leg.stops[stopIndex],
      };

      const stopEquipments = stop.equipments || [];
      const equipmentIndex = stopEquipments.findIndex(
        (eq) => eq._id === equipmentId,
      );
      stop.equipments = [
        ...stop.equipments.slice(0, equipmentIndex),
        ...stop.equipments.slice(equipmentIndex + 1),
      ];

      leg.stops = [
        ...leg.stops.slice(0, stopIndex),
        stop,
        ...leg.stops.slice(stopIndex + 1),
      ];

      return {
        trip: {
          ...prevState.trip,
          legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)],
        },
      };
    });
  };

  handleStopIntentSelect = (legSequence, stopSequence, intent) => {
    this.setState((prevState) => {
      const {
        trip: { legs },
      } = prevState;

      const index = legs.findIndex((l) => l.sequence === legSequence);
      const leg = _.cloneDeep(legs[index]);
      const stopIndex = leg.stops.findIndex((s) => s.sequence === stopSequence);

      const stop = {
        ...leg.stops[stopIndex],
        ...intent,
      };
      leg.stops = [
        ...leg.stops.slice(0, stopIndex),
        stop,
        ...leg.stops.slice(stopIndex + 1),
      ];
      return {
        trip: {
          ...prevState.trip,
          legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)],
        },
      };
    });
  };

  handleStopLocationNameChange = (legSequence, stopSequence, value) => {
    this.setState((prevState) => {
      const {
        trip: { legs },
      } = prevState;
      const index = legs.findIndex((l) => l.sequence === legSequence);
      const leg = _.cloneDeep(legs[index]);

      const stopIndex = leg.stops.findIndex((s) => s.sequence === stopSequence);
      const stop = leg.stops[stopIndex];

      stop.location = stop.location || { address: '', name: '' };
      stop.location.name = value;

      leg.stops = [
        ...leg.stops.slice(0, stopIndex),
        stop,
        ...leg.stops.slice(stopIndex + 1),
      ];

      return {
        trip: {
          ...prevState.trip,
          legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)],
        },
      };
    });
  };

  handleStopLocationAddressChange = (legSequence, stopSequence) => {
    this.setState((prevState) => {
      const {
        trip: { legs },
      } = prevState;
      const index = legs.findIndex((l) => l.sequence === legSequence);
      const leg = _.cloneDeep(legs[index]);

      const stopIndex = leg.stops.findIndex((s) => s.sequence === stopSequence);
      const stop = leg.stops[stopIndex];
      const location = stop.location;
      stop.location = {
        ...location,
        address: '',
      };

      leg.stops = [
        ...leg.stops.slice(0, stopIndex),
        stop,
        ...leg.stops.slice(stopIndex + 1),
      ];

      return {
        trip: {
          ...prevState.trip,
          legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)],
        },
      };
    });
  };

  handleStopLocationSelect = (legSequence, stopSequence, item) => {
    this.setState((prevState) => {
      const {
        trip: { legs },
      } = prevState;
      const index = legs.findIndex((l) => l.sequence === legSequence);
      const leg = _.cloneDeep(legs[index]);

      const stopIndex = leg.stops.findIndex((s) => s.sequence === stopSequence);
      const stop = leg.stops[stopIndex];
      const location = stop.location;
      const name = location.name || item.name;
      stop.location = {
        ...item,
        name,
      };

      leg.stops = [
        ...leg.stops.slice(0, stopIndex),
        stop,
        ...leg.stops.slice(stopIndex + 1),
      ];

      return {
        trip: {
          ...prevState.trip,
          legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)],
        },
      };
    });
  };

  handleStopRemove = (legSequence, stopSequence) => {
    this.setState((prevState) => {
      const {
        trip: { legs },
      } = prevState;
      const index = legs.findIndex((l) => l.sequence === legSequence);
      const leg = _.cloneDeep(legs[index]);

      const stopIndex = leg.stops.findIndex((s) => s.sequence === stopSequence);

      leg.stops = [
        ...leg.stops.slice(0, stopIndex),
        ...leg.stops.slice(stopIndex + 1),
      ].map((stop, i) => ({
        ...stop,
        sequence: i + 1,
      }));

      return {
        trip: {
          ...prevState.trip,
          legs: [...legs.slice(0, index), leg, ...legs.slice(index + 1)],
        },
      };
    });
  };

  saveDisabled() {
    const {
      trip: { name: tripName },
      formStatus: { pending },
    } = this.state;

    if (pending) {
      return true;
    }

    if (tripName) {
      return false;
    }
    return true;
  }

  validate(state, _isApplying = false) {
    const {
      trip: { legs },
    } = state;
    const { t } = this.props;
    const validationResult = {};

    validationResult.legs = legs.reduce((runningErrors, leg) => {
      const { stops } = leg;
      const legErrors = {};
      legErrors.base = [];
      if (stops.length === 0) {
        legErrors.base = [t('ADD_ATLEAST_ONE_STOP')];
        validationResult.hasErrors = true;
      } else {
        let hasStopError = false;
        legErrors.stops = stops.map((stop) => {
          const location = stop.location || { address: '', name: '' };
          const stopErrors = {};
          if (!stop.action) {
            validationResult.hasErrors = true;
            stopErrors.action = t('PLEASE_SELECT_INTENT');
          }
          if (!location.address) {
            stopErrors.address = t('SET_AN_ADDRESS_FOR_STOP');
            hasStopError = true;
          }
          return stopErrors;
        });

        if (hasStopError) {
          legErrors.base.push(t('FILL_ALL_REQUIRED_FIELDS_FOR_STOPS'));
          validationResult.hasErrors = true;
        }
      }

      return runningErrors.concat(legErrors);
    }, []);

    return validationResult;
  }

  transformPayload() {
    const {
      trip: { _id, name, legs: currentLegs },
    } = this.state;

    const legs = currentLegs.map((leg) => {
      const {
        status,
        shipment_type,
        carrier_id,
        sequence: legSeqence,
        stops: legStops,
      } = leg;

      const carrier = carrier_id;

      const stops = legStops.map((stop) => {
        const { sequence, action, empty, location, equipments } = stop;

        return {
          sequence,
          action,
          empty,
          location,
          equipments,
          carrier,
        };
      });

      if (shipment_type === 'drayage') {
        return {
          sequence: legSeqence,
          shipment_type,
          carrier,
          carrier_id,
          stops,
          status,
        };
      }

      return {
        sequence: legSeqence,
        shipment_type,
        carrier,
        carrier_id,
        stops,
      };
    });

    const trip = {
      name,
      legs,
    };

    if (_id) {
      trip._id = _id;
    }

    return trip;
  }

  handleMatrixChange = (matrixData) => {
    const {
      trip: { legs },
    } = this.state;
    let tempLegs = _.cloneDeep(legs);

    const eqIdsArr = Object.keys(matrixData);
    eqIdsArr.forEach((eqId) => {
      const eqMatrix = matrixData[eqId];
      const legsIdsArr = Object.keys(eqMatrix);

      legsIdsArr.forEach((legId) => {
        const legStopsArr = matrixData[eqId][legId];
        const foundIndex = tempLegs.findIndex(
          (leg) => leg.sequence === Number(legId.replace(/\D+/g, '')),
        );
        const foundLeg = tempLegs[foundIndex];
        let stops = foundLeg.stops.map((stop) => {
          let equipments = [...stop.equipments];
          if (
            legStopsArr[`stop_${stop.sequence}`] &&
            equipments.every((eq) => eq.equipment_id !== eqId)
          ) {
            equipments.push(
              this.props.equipments.find((eq) => eq.equipment_id === eqId),
            );
          } else if (
            legStopsArr[`stop_${stop.sequence}`] === false &&
            equipments.some((eq) => eq.equipment_id === eqId)
          ) {
            equipments = equipments.filter((eq) => eq.equipment_id !== eqId);
          }

          return {
            ...stop,
            equipments,
          };
        });

        tempLegs[foundIndex] = {
          ...foundLeg,
          stops,
        };
      });
    });

    this.setState((prevState) => {
      return {
        trip: {
          ...prevState.trip,
          legs: tempLegs,
        },
      };
    });
  };

  renderEmptyLegs() {
    const { t } = this.props;
    return (
      <div className="p-8 bg-white shadow rounded">
        <div className="">
          <p>{t('CREATE_LEG_TO_START')}</p>
          <button
            onClick={this.handleLegAdd}
            className="button button-hollow-green"
          >
            <i className="fa fa-plus mr-2" />
            {titleize(t('LEG', { count: 1 }))}
          </button>
        </div>
      </div>
    );
  }

  renderLegs() {
    const {
      trip: { legs },
    } = this.state;

    if (legs.length === 0) {
      return this.renderEmptyLegs();
    }
    const { t, shipperId } = this.props;
    return (
      <div>
        <div className="mb-6">
          {legs.map((leg, i) => (
            <Leg
              key={i}
              leg={leg}
              shipperId={shipperId}
              validationResult={this.state.validationResult.legs[i]}
              onCarrierFieldChange={this.handleCarrierFieldChange}
              onCarrierRemove={this.handleCarrierRemove}
              onCarrierSelect={this.handleCarrierSelect}
              onLegEquipmentAdd={this.handleLegEquipmentAdd}
              onLegRemove={this.handleLegRemove}
              onLegTypeChange={this.handleLegTypeChange}
              onStatusChange={this.handleStatusChange}
              onStopAdd={this.handleStopAdd}
              onStopIntentSelect={this.handleStopIntentSelect}
              onStopEquipmentAdd={this.handleStopEquipmentAdd}
              onStopEquipmentRemove={this.handleStopEquipmentRemove}
              onStopLocationAddressChange={this.handleStopLocationAddressChange}
              onStopLocationNameChange={this.handleStopLocationNameChange}
              onStopLocationSelect={this.handleStopLocationSelect}
              onStopRemove={this.handleStopRemove}
            />
          ))}
        </div>
        <div>
          <button
            onClick={this.handleLegAdd}
            className="button button-hollow-green bg-white"
          >
            <i className="fa fa-plus mr-2" />
            {titleize(t('LEG', { count: 1 }))}
          </button>
        </div>
      </div>
    );
  }

  renderEquipment() {
    const equipments = this.props.equipments || [];
    const { t } = this.props;
    const {
      trip: { legs },
    } = this.state;

    if (!equipments.length) {
      return null;
    }

    return (
      <div>
        <div className="flex items-center space-x-1">
          <h3 className="text-xl mt-0 mb-4">{titleize(t('EQUIPMENT'))}</h3>
          <OverlayTrigger placement="top" overlay={equipmentTip}>
            <div className="mb-3">
              <InfoIcon size="medium" />
            </div>
          </OverlayTrigger>
        </div>

        <TripPlannerEquipmentMatrix
          equipments={equipments}
          legs={legs}
          onChangeData={this.handleMatrixChange}
        />
      </div>
    );
  }

  renderCreateActionButtons() {
    const {
      formStatus: { pending },
    } = this.state;
    const { t } = this.props;
    return (
      <div className="flex my-4">
        <input
          type="text"
          placeholder={t('TRIP_NAME')}
          value={this.state.trip.name}
          className="form-field mr-2"
          onChange={this.handleTripNameChange}
        />
        <button
          type="submit"
          className="button button-hollow-green mr-2"
          disabled={this.saveDisabled()}
          onClick={this.handleSaveTrip}
        >
          {!pending && <span>{t('CREATE_TRIP')}</span>}
          {pending && (
            <div className="flex items-center">
              <i className="fa fa-spin fa-circle-o-notch mr-2" />
              <span>{t('SAVING')}...</span>
            </div>
          )}
        </button>
        <button
          type="submit"
          className="button button-green mr-2"
          disabled={this.saveDisabled()}
          onClick={this.handleSaveAndApplyTrip}
        >
          {!pending && <span>{t('CREATE_AND_APPLY_TRIP')}</span>}
          {pending && (
            <div className="flex items-center">
              <i className="fa fa-spin fa-circle-o-notch mr-2" />
              <span>{t('SAVING')}...</span>
            </div>
          )}
        </button>
      </div>
    );
  }

  renderUpdateActionButtons() {
    const {
      formStatus: { pending },
    } = this.state;
    const { t } = this.props;
    return (
      <div className="flex my-4">
        {!this.state.showSaveAsSection && [
          <button
            key="save"
            type="submit"
            className="button button-hollow-green mr-2"
            onClick={this.handleSaveClick}
          >
            {t('SAVE_TRIP')}
          </button>,
          <button
            key="saveas"
            type="submit"
            className="button button-hollow-green mr-2"
            disabled={this.saveDisabled()}
            onClick={this.handleSaveAsClick}
          >
            {t('SAVE_TRIP_AS')}
          </button>,
        ]}
        {this.state.showSaveAsSection && (
          <div>
            <input
              type="text"
              placeholder={t('TRIP_NAME')}
              value={this.state.trip.name}
              className="form-field mr-2"
              onChange={this.handleTripNameChange}
            />
            <button
              type="submit"
              className="button button-hollow-green mr-2"
              disabled={this.saveDisabled()}
              onClick={this.handleSaveClick}
            >
              {!pending && <span>{t('SAVE_TRIP')}</span>}
              {pending && (
                <div className="flex items-center">
                  <i className="fa fa-spin fa-circle-o-notch mr-2" />
                  <span>{t('SAVING')}...</span>
                </div>
              )}
            </button>
          </div>
        )}
        <button
          key="save-and-apply"
          type="submit"
          className="button button-green mr-2"
          disabled={this.saveDisabled()}
          onClick={this.handleSaveAndApplyTrip}
        >
          {t('SAVE_TRIP_AND_APPLY_ORDER')}
        </button>
        ,
      </div>
    );
  }

  renderActions() {
    const {
      trip: { _id },
      t,
    } = this.props;

    const { formStatus, validationResult } = this.state;

    return (
      <div className="flex flex-wrap my-4 -mx-4 justify-end">
        <div className="flex-auto">
          {validationResult.hasErrors && (
            <div className="w-full">
              <ErrorAlert
                title={
                  validationResult.emptyStatus || t('FILL_ALL_REQUIRED_FIELDS')
                }
                canClose
                onClose={this.handleCloseErrorAlert}
              />
            </div>
          )}
          {formStatus.completed && !formStatus.success && (
            <div className="w-full">
              <ErrorAlert
                title={t('TRIP_FAILED_TO_SAVE')}
                canClose
                onClose={this.handleCloseSaveErrorAlert}
              />
            </div>
          )}
          {formStatus.completed && formStatus.success && (
            <div className="w-full">
              <Alert
                title={t('TRIP_SAVED_SUCCESSFULLY')}
                type="success"
                canClose
                onClose={this.handleCloseSuccessAlert}
              />
            </div>
          )}
        </div>
        <div className="px-4 md:w-2/5 w-full">
          <ProTip message={t('SAVE_TRIP_PROTIPS')} />
        </div>
        <div className="px-4">
          {_id && this.renderUpdateActionButtons()}

          {!_id && this.renderCreateActionButtons()}
        </div>
      </div>
    );
  }

  render() {
    const { t } = this.props;
    return (
      <div className="flex flex-col">
        <div className="flex flex-wrap -mx-2">
          <div className="md:w-2/3 lg:w-3/5 w-full px-2">
            <h2 className="text-2xl mt-0 mb-4">{this.getHeading()}</h2>
            <div>{this.renderEquipment()}</div>
            <div
              className="overflow-scroll"
              style={{ height: 'calc(100vh - 25rem)' }}
            >
              <div className="flex items-center">
                <h3 className="text-xl mt-0 mb-2">
                  {titleize(t('LEG', { count: 2 }))}
                </h3>
                <button
                  title={t('ADD_A_LEG')}
                  className="flex-grow-0 flex-shrink-0 ml-2 w-8 h-8 mb-4 border border-green border-solid hover:bg-green hover:text-white text-green rounded-full"
                  onClick={this.handleLegAdd}
                >
                  <i className="fa fa-plus mr-0" />
                </button>
              </div>
              {this.renderLegs()}
            </div>
          </div>
          <div className="md:w-1/3 lg:w-2/5 w-full px-2">
            <Map waypoints={[]} center={center} height="100%" />
          </div>
        </div>
        <div className="flex flex-col items-end">{this.renderActions()}</div>
      </div>
    );
  }
}

export default withTranslation()(TripPlannerForm);
